import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Copy, Send, Save, Undo2, CheckCheck, BookOpen } from "lucide-react";
import { API, api, shortDate } from "../lib/api";
import { NeonButton } from "./common";
import { useAuth } from "../lib/auth";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";

async function streamDraft(disputeId, tone, onDelta) {
  const res = await fetch(`${API}/disputes/${disputeId}/representment`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tone }),
  });
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "", final = null;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      if (!p.startsWith("data: ")) continue;
      const msg = JSON.parse(p.slice(6));
      if (msg.delta) onDelta(msg.delta);
      if (msg.error) throw new Error(msg.error);
      if (msg.done) final = msg.representment;
    }
  }
  return final;
}

const STATUS_C = { draft: "#FFB800", staged: "#FF2BD6", submitted: "#CCFF00", submitted_dry_run: "#37E5D7" };

const Playbook = ({ reason }) => {
  const [t, setT] = useState(null);
  useEffect(() => { api.get("/templates").then((r) => setT(r.data.find((x) => x.reason === reason))); }, [reason]);
  if (!t) return null;
  return (
    <details className="mt-4 border border-white/10 p-3" data-testid="reason-playbook">
      <summary className="font-mono text-[10px] tracking-[0.16em] text-white/60 cursor-pointer flex items-center gap-2"><BookOpen className="w-3 h-3" /> REASON-CODE PLAYBOOK · {t.label} · VISA {t.codes.visa} / MC {t.codes.mastercard} / AMEX {t.codes.amex}</summary>
      <p className="label-xs mt-3">Required evidence</p>
      <p className="font-mono text-[11px] text-white/70 mt-1">{t.required_evidence.join(" · ")}</p>
      <p className="label-xs mt-3">Proven winning language</p>
      <ul className="mt-1 space-y-1">{t.winning_language.map((l) => <li key={l} className="text-xs text-white/70">“{l}”</li>)}</ul>
    </details>
  );
};

export const RepresentmentDrafter = ({ disputeId, reason, issuer, dueBy, drafts, onSaved }) => {
  const { isOwner, canWrite } = useAuth();
  const [tone, setTone] = useState("formal");
  const [text, setText] = useState(drafts[0]?.content || "");
  const [current, setCurrent] = useState(drafts[0] || null);
  const [busy, setBusy] = useState(false);
  const [score, setScore] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [dry, setDry] = useState(true);
  useEffect(() => { api.get(`/disputes/${disputeId}/draft-score`).then((r) => setScore(r.data)); api.get("/settings").then((r) => setDry(r.data.dry_run ?? true)); }, [disputeId, current?.status]);

  const act = async (fn, msg) => {
    try { const { data } = await fn(); setCurrent(data.content ? data : { ...current, ...data }); toast.success(msg); onSaved?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };
  const generate = async () => {
    setBusy(true); setText(""); setCurrent(null);
    try { const rep = await streamDraft(disputeId, tone, (d) => setText((t) => t + d)); setCurrent(rep); toast.success("Representment drafted — review, edit, then stage"); }
    catch (e) { toast.error(`Drafting failed: ${e.message}`); }
    finally { setBusy(false); }
  };
  const editable = current && ["draft", "staged"].includes(current.status) && !busy;
  const dirty = current && text !== current.content;

  return (
    <div data-testid="representment-drafter">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Select value={tone} onValueChange={setTone}>
          <SelectTrigger className="w-36 h-9 bg-field border-[#1A3832] font-mono text-xs" data-testid="tone-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-panel border-[#1A3832]">
            {["formal", "assertive", "concise"].map((t) => <SelectItem key={t} value={t} data-testid={`tone-${t}`}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <NeonButton onClick={generate} disabled={busy || !canWrite} data-testid="generate-representment-btn">
          <Sparkles className="w-3.5 h-3.5" /> {busy ? "Drafting…" : current ? "Regenerate" : "Generate draft"}
        </NeonButton>
        {score && (
          <span className="font-mono text-[10px] text-white/60 ml-auto" data-testid="draft-score">
            DRAFT SCORE <span className="text-brand text-sm font-bold">{score.score}%</span> · {score.historical_win_rate}% hist. win on {score.samples} {score.scope} · {score.ai_letter_outcomes} AI-letter outcomes
          </span>
        )}
      </div>
      <div className={`border border-[#12302A] bg-canvas ${busy ? "tracing" : ""}`}>
        {editable ? (
          <textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="representment-editor"
            className="w-full min-h-[300px] max-h-[520px] bg-transparent p-5 font-mono text-[12px] leading-relaxed text-[#DDD] outline-none resize-y" />
        ) : (
          <pre className={`min-h-[260px] max-h-[520px] overflow-y-auto whitespace-pre-wrap p-5 text-[12px] leading-relaxed text-[#DDD] ${busy ? "caret" : ""}`} data-testid="representment-output">
            {text || (!busy && <span className="text-white/30">// Claude drafts from your signed evidence, the reason-code playbook and language proven in past wins. You review and edit before anything is submitted.</span>)}
          </pre>
        )}
      </div>
      {current && !busy && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
          <span className="font-mono text-[10px] text-white/40">
            <span style={{ color: STATUS_C[current.status] }} data-testid="representment-status">{current.status.replace(/_/g, " ").toUpperCase()}</span> · {current.model} · {shortDate(current.created_at)} · {(current.versions || []).length} prior version(s){current.outcome ? ` · outcome ${current.outcome}` : ""}
          </span>
          <div className="flex flex-wrap gap-2">
            <NeonButton variant="ghost" onClick={() => { navigator.clipboard.writeText(text); toast.success("Copied"); }} data-testid="copy-representment-btn"><Copy className="w-3 h-3" /> Copy</NeonButton>
            {dirty && <NeonButton variant="ghost" onClick={() => act(() => api.put(`/representments/${current.id}`, { content: text }), "Edits saved (previous version kept)")} data-testid="save-representment-btn"><Save className="w-3 h-3" /> Save edits</NeonButton>}
            {current.status === "draft" && !dirty && <NeonButton variant="ghost" onClick={() => act(() => api.post(`/representments/${current.id}/stage`), "Staged for approval")} data-testid="stage-representment-btn"><CheckCheck className="w-3 h-3" /> Stage for approval</NeonButton>}
            {current.status === "staged" && (isOwner ? <NeonButton onClick={() => setConfirm(true)} data-testid="submit-representment-btn"><Send className="w-3 h-3" /> Approve & submit</NeonButton>
              : <span className="font-mono text-[10px] text-[#FF2BD6] self-center" data-testid="awaiting-owner-approval">AWAITING OWNER APPROVAL</span>)}
            {isOwner && ["staged", "submitted_dry_run", "submitted"].includes(current.status) && <NeonButton variant="danger" onClick={() => act(() => api.post(`/representments/${current.id}/revert`), "Reverted to draft")} data-testid="revert-representment-btn"><Undo2 className="w-3 h-3" /> Revert</NeonButton>}
          </div>
        </div>
      )}
      <Playbook reason={reason} />
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="bg-canvas border-white/15" data-testid="submit-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-mono">{dry ? "Dry-run submission" : "Submit representment"}</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              This will submit your representment to {issuer} for dispute {disputeId}, due by {dueBy?.slice(0, 10)}.{" "}
              {dry ? "DRY-RUN is ON: the full workflow is recorded but nothing leaves the platform." : "DRY-RUN is OFF: the dispute moves to under review (issuer API delivery is MOCKED)."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-mono text-xs" data-testid="submit-confirm-no">No</AlertDialogCancel>
            <AlertDialogAction className="font-mono text-xs bg-brand text-black hover:bg-brand/90" data-testid="submit-confirm-yes"
              onClick={() => act(() => api.post(`/representments/${current.id}/submit`), dry ? "Dry-run submission recorded" : "Submitted to issuer")}>Yes, submit</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
